import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { Search, X } from "lucide-react";
import debounce from "lodash/debounce";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import { Input } from "../ui/Input";

interface MainBarSearchProps {
  onQueryChange: (query: string) => void;
  initialQuery?: string;
  debounceDelay?: number;
  placeholder?: string;
  iconSize?: number;
  className?: string;
}

interface SearchState {
  value: string;
  lastEmitted: string;
}

type SearchAction = {
  type: "draft" | "emitted" | "external";
  value: string;
};

const searchReducer = (
  state: SearchState,
  action: SearchAction
): SearchState => {
  if (action.type === "draft") {
    return { ...state, value: action.value };
  }

  if (action.type === "emitted") {
    return { ...state, lastEmitted: action.value };
  }

  if (action.value === state.lastEmitted) {
    return state;
  }

  return { value: action.value, lastEmitted: action.value };
};

export const MainBarSearch: React.FC<MainBarSearchProps> = ({
  onQueryChange,
  initialQuery = "",
  debounceDelay = 750,
  placeholder = "Search",
  iconSize = ICON_SIZE,
  className = "",
}) => {
  const [searchState, dispatchSearch] = useReducer(searchReducer, {
    value: initialQuery,
    lastEmitted: initialQuery,
  });

  // Sync local state when the external value changes (e.g. navigation, URL
  // param change).  Skip the echo: when our own debounced callback updates the
  // URL, nuqs feeds the value back as a new `initialQuery` — we already have
  // that value in local state, so writing it again would clobber mid-typing.
  useEffect(() => {
    dispatchSearch({ type: "external", value: initialQuery });
  }, [initialQuery]);

  const emitQuery = useCallback(
    (value: string) => {
      if (value === "" || value.length >= 2) {
        dispatchSearch({ type: "emitted", value });
        onQueryChange(value);
      }
    },
    [onQueryChange]
  );

  const debouncedQueryChange = useMemo(
    () => debounce(emitQuery, debounceDelay),
    [emitQuery, debounceDelay]
  );

  useEffect(() => {
    return () => {
      debouncedQueryChange.cancel();
    };
  }, [debouncedQueryChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatchSearch({ type: "draft", value });
    debouncedQueryChange(value);
  };

  const handleClearSearch = () => {
    debouncedQueryChange.cancel();
    dispatchSearch({ type: "draft", value: "" });
    dispatchSearch({ type: "emitted", value: "" });
    onQueryChange("");
  };

  return (
    <Input
      className={cn("w-full", className)}
      type="text"
      layout="horizontal"
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={handleInputChange}
      value={searchState.value}
      startIcon={<Search size={iconSize} className="text-gray-400" />}
      endContent={
        searchState.value && (
          <button
            type="button"
            onClick={handleClearSearch}
            aria-label="Clear search"
          >
            <X size={iconSize} className="text-gray-400" aria-hidden="true" />
          </button>
        )
      }
    />
  );
};

export default MainBarSearch;
